import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { isValidEmail, showToast } from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import { loginAction } from "../../Store/Actions/AuthActions";
import SignUp from "./Signup";
import ForgotPassword from "./ForgotPassword";
import GoogleLoginBtn from "../Component/GoogleLoginBtn";

const Login = ({
  asModal = false,
  redirect = false,
  onSuccess = null,
  updateTitle = null,
  onClose = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const { redirectUrl } = location.state || {};

  const [data, setData] = useState({
    email: "",
    password: "",
    type: "credentials",
  });
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const [selectedStep, setSelectedStep] = useState("Login");

  const validateForm = () => {
    const errs = {};

    if (!data.email || data.email === "") {
      errs.email = "Please enter your email address";
    } else if (data.email && !isValidEmail(data.email)) {
      errs.email = "Please enter a valid email address";
    }

    if (!data.password || data.password === "") {
      errs.password = "Please enter your password";
    }

    return errs;
  };

  const submitForm = () => {
    const errs = validateForm();
    setErrors(errs);

    if (!loading && Object.entries(errs).length === 0) {
      setValidated(false);
      setLoading(true);

      trackEvent("web_login_submit", { method: "email" });
      loginAction({ ...data, device_type: "web" }, dispatch)
        .then((res) => {
          if (res.type === "success") {
            trackEvent("web_login_success", { method: "email" });
            if (onSuccess) {
              onSuccess();
            }
          } else {
            trackEvent("web_login_failed", { method: "email" });
          }

          const type = res.type === "failure" ? "error" : res.type;

          if (res.message || type === "error") {
            showToast(type, res.message || "Something went wrong");
          }
        })
        .catch((err) => {
          showToast("error", err?.message || "Something went wrong");
        })
        .finally(() => setLoading(false));
    } else {
      setValidated(true);
    }
  };

  const onChagne = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const redirectTo = (url) => {
    navigate(url);
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (redirect) {
        if (redirectUrl) {
          redirectTo(redirectUrl);
        }
      } else if (!onSuccess) {
        if (user.type === "user") {
          redirectTo("/user/spots");
        } else {
          redirectTo("/admin/spots");
        }
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (updateTitle) {
      updateTitle(selectedStep);
    }
  }, [selectedStep]);

  return (
    <>
      <div className="login-form-wrapper">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h2>{selectedStep}</h2>
            <p>
              {selectedStep === "Forgot Password" ? (
                <>Reset your password</>
              ) : (
                <>
                  Get started with <b>Spotie</b> below
                </>
              )}
            </p>
          </div>

          {/* <span className="close-icon as-link" onClick={() => onClose()}>
            <i className="fas fa-times" />
          </span> */}
        </div>

        {selectedStep === "Login" && (
          <Form
            validated={validated}
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <Form.Group className="form-group with-icon">
              <i className="far fa-envelope" />
              <Form.Control
                type="email"
                name="email"
                value={data.email}
                required
                placeholder="Your Email"
                onChange={(e) => onChagne(e)}
              />
              {errors.email && (
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="form-group with-icon">
              <i className="fas fa-lock" />
              <Form.Control
                type={showPassword ? "text" : "password"}
                name="password"
                required
                value={data.password}
                placeholder="Your Password"
                onChange={(e) => onChagne(e)}
              />
              <button
                type="button"
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
              >
                <i className={`far ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
              {errors.password && (
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <div class="button-wrapper">
              <Button
                className="btn btn-medium solid"
                onClick={() => submitForm()}
              >
                {!loading && <span>Login</span>}

                {loading && (
                  <i className="fas fa-spinner fa-pulse loadingIcon" />
                )}
              </Button>
            </div>

            <div className="social-login-btn">
              <span
                className="forgot-link"
                onClick={() => setSelectedStep("Forgot Password")}
              >
                Forgot Password
              </span>

              <div>
                <GoogleLoginBtn
                  onSuccess={() => {
                    if (onSuccess) {
                      onSuccess();
                    }
                  }}
                />
              </div>

              {/* <Button onClick={() => {}}>
                <span>
                  <i className="fab fa-google" /> Sign in with Google
                </span>
              </Button> */}
            </div>

            <div className="guide-links ml-auto">
              Don't have an account?
              <span className="link" onClick={() => setSelectedStep("Signup")}>
                Create Account
              </span>
            </div>
          </Form>
        )}

        {selectedStep === "Signup" && (
          <SignUp
            asModal={true}
            redirect={redirect}
            changeStep={(step) => setSelectedStep(step)}
            onSuccess={() => onSuccess()}
          />
        )}

        {selectedStep === "Forgot Password" && (
          <ForgotPassword changeStep={(step) => setSelectedStep(step)} />
        )}
      </div>
    </>
  );
};

export default Login;
